import {ServiceBase} from "../service-base";
import {BookingSearchResultViewMode, IAgencyBookingSearchService} from "./agency-booking-search.service.interface";
import {
    BookingSearchStrategyType,
    IBookingSearchStrategyViewModel
} from "./strategies/booking-search.strategy.interface";
import {IServiceFactory} from "../service-factory.interface";
import {SearchByRecordLocatorStrategy} from "./strategies/by-record-locator/search-by-record-locator.strategy";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {SearchByEmailStrategy} from "./strategies/by-email/search-by-email.strategy";
import {SearchByBookingDateStrategy} from "./strategies/by-booking-date/search-by-booking-date.strategy";
import {SearchByContactStrategy} from "./strategies/by-contact/search-by-contact.strategy";
import {SearchByLastNameStrategy} from "./strategies/by-last-name/search-by-last-name.strategy";
import {SearchByPhoneNumberStrategy} from "./strategies/by-phone/search-by-phone-number.strategy";
import {UserLocalStorageKeys} from "../storage/user-local-storage/user-local-storage-keys";
import {RecentBookingsStrategy} from "./strategies/recently-bookings/recent-bookings.strategy";

export class AgencyBookingSearchService extends ServiceBase implements IAgencyBookingSearchService {
    constructor(services: IServiceFactory) {
        super(services);
        this.searchStrategies = [
            new RecentBookingsStrategy(services),
            new SearchByRecordLocatorStrategy(services),
            new SearchByEmailStrategy(services),
            new SearchByBookingDateStrategy(services),
            new SearchByLastNameStrategy(services),
            new SearchByPhoneNumberStrategy(services),
            new SearchByContactStrategy(services),
        ];
        
        this._resultViewMode = parseInt(this.services.userLocalStorage.getItem(UserLocalStorageKeys.BookingsSearchCurrentViewMode) ?? BookingSearchResultViewMode.CardsView.toString());

        makeObservable<this, '_currentStrategy' | '_resultViewMode'>(this, {
            _currentStrategy: observable.ref,
            _resultViewMode: observable.ref
        });

       this._currentStrategy = this._getInitialStrategy();
       reaction(() => this.services.user.isAuthorized, () => {
           this.searchStrategies.forEach(s => {
               s.clearAllFilters();
               s.clearResults();
           });
       });

       if(this.services.user.isAuthorized
           && this.services.navigator.routes.agency.bookingSearch.isActive
           && this.currentStrategy.hasValidFilters
           && this.currentStrategy.pages.length === 0
       ) {
           //this is for when the user refresh the page so we can restore the results of the current filters.
           this.currentStrategy.applySearch();
       }
    }

    private _resultViewMode: BookingSearchResultViewMode = BookingSearchResultViewMode.CardsView;
    get resultViewMode(): BookingSearchResultViewMode {
        return this._resultViewMode;
    }
    set resultViewMode(value: BookingSearchResultViewMode) {
        runInAction(() => {
            this._resultViewMode = value;
            this.services.userLocalStorage.setItem(UserLocalStorageKeys.BookingsSearchCurrentViewMode, value.toString());
        });
    }

    getResultViewModeDescription(viewMode: BookingSearchResultViewMode): string {
        if(viewMode === BookingSearchResultViewMode.CardsView) {
            return this.services.language.translate('Cards view');
        } else {
            return this.services.language.translate('Table view');
        }
    }

    readonly searchStrategies: IBookingSearchStrategyViewModel[];

    private _currentStrategy: IBookingSearchStrategyViewModel;
    get currentStrategy(): IBookingSearchStrategyViewModel {
        return this._currentStrategy;
    }

    private get _currentFilterStorageKey(): UserLocalStorageKeys {
        return UserLocalStorageKeys.BookingsSearchFiltersCurrentFilter;
    }

    setCurrentStrategy(strategyType: BookingSearchStrategyType): void {
        const strategy = this.searchStrategies.find(s => s.type === strategyType);
        if(strategy) {
            runInAction(() => {
                this._currentStrategy.resetErrorsValidation();
                this._currentStrategy = strategy;
                this.services.userLocalStorage.setItem(this._currentFilterStorageKey, strategyType.toString());
            });
        }
    }

    private _getInitialStrategy(): IBookingSearchStrategyViewModel {
        const currentFilterType = this.services.userLocalStorage.getItem(this._currentFilterStorageKey);
        if(currentFilterType) {
            const currentFilterTypeAsNumber = parseInt(currentFilterType);
            const strategy = this.searchStrategies.find(s => s.type === currentFilterTypeAsNumber);
            if(strategy) {
                return strategy;
            }
        }

        return this.searchStrategies[0]
    }

    async goToRecentBookings(): Promise<void> {
        this.services.navigator.routes.agency.bookingSearch.activate();
        this.setCurrentStrategy(BookingSearchStrategyType.RecentBookings);
        await this.currentStrategy.applySearch();
    }


}
